import { h } from 'preact';
import { useRef, useEffect, useMemo } from 'preact/hooks';
import PropTypes from 'prop-types';
import Siema from 'siema';

import { IconAlert } from '../../1-foundation/icons/icons';
import Cta from '../../2-atoms/cta/cta';

const SkyItPanel = ({
  title,
  description,
  hasBorder,
  hasImageCentered,
  isReversed,
  paginationItems,
  hasInfo,
  images,
  intcmpCta,
  ctaPrimaryHref,
  onCtaPrimaryClick,
  ctaPrimaryLabel,
  ctaSecondaryLabel,
  onCtaSecondaryClick,
  selectedPage = 0,
}) => {
  const borderModifier = hasBorder ? ' c-sky-it-panel--with-border' : '';
  const imageModifier = images?.length ? ' c-sky-it-panel--with-image' : '';
  const paginationModifier = paginationItems?.length ? ' c-sky-it-panel--with-pagination' : '';
  const imageCenteredModifier = hasImageCentered ? ' c-sky-it-panel--image-centered' : '';
  const reverseModifier = isReversed ? ' c-sky-it-panel--reversed' : '';
  const classNames = `c-sky-it-panel${borderModifier}${imageModifier}${paginationModifier}${reverseModifier}${imageCenteredModifier}`;
  const ctaPrimaryTag = onCtaPrimaryClick ? 'button' : 'a';

  const hasSingleImage = images?.length === 1;
  const hasMultipleImages = images?.length > 1;
  const hasPagination = paginationItems && hasMultipleImages;
  const sliderRef = useRef();
  const sliderInstance = useRef();

  const imagesGallery = useMemo(() => {
    return (
      <div ref={sliderRef} className="c-sky-it-panel__image c-sky-it-panel__image--swipe">
        {images?.map((image, index) => (
          <div key={index}>
            <picture>
              <source media="(max-width: 767px)" srcSet={image.mobileSrcSet} />
              <source media="(min-width: 768px)" srcSet={image.tabletSrcSet} />
              <img alt={image.alt} src={image.imgSrc} />
            </picture>
          </div>
        ))}
      </div>
    );
  }, [images?.length]);

  useEffect(() => {
    if (hasPagination) {
      sliderInstance.current = new Siema({
        selector: sliderRef.current,
        perPage: 1,
        onChange: () => {
          paginationItems[sliderInstance.current.currentSlide].onClick();
        },
      });

      return () => sliderInstance.current.destroy();
    }
  }, []);

  return (
    <div className={classNames}>
      <div className="c-sky-it-panel__content">
        {hasInfo && (
          <div className="c-sky-it-panel__info">
            <IconAlert modifiers={['neutral', 'alert', 'alert', 'small']} classNames="c-sky-it-box__alert-icon" />
          </div>
        )}
        <h3 className="c-sky-it-panel__title">{title}</h3>
        <p className="c-sky-it-panel__abstract" dangerouslySetInnerHTML={{ __html: description }} />
      </div>
      <div className="c-sky-it-panel__footer">
        {ctaPrimaryLabel && (
          <Cta
            modifiers={['sky-it-primary']}
            tag={ctaPrimaryTag}
            onClick={onCtaPrimaryClick}
            href={ctaPrimaryHref}
            intcmpCta={intcmpCta}
          >
            <span className="c-cta__text">{ctaPrimaryLabel}</span>
          </Cta>
        )}
        {ctaSecondaryLabel && (
          <Cta modifiers={['sky-it-no-outline', 'capitalized']} tag="button" onClick={onCtaSecondaryClick}>
            <span className="c-cta__text">{ctaSecondaryLabel}</span>
          </Cta>
        )}
      </div>
      {hasPagination && imagesGallery}

      {!hasPagination && hasSingleImage && (
        <div className="c-sky-it-panel__image">
          <picture>
            <source media="(max-width: 767px)" srcSet={images[0].mobileSrcSet} />
            <source media="(min-width: 768px)" srcSet={images[0].tabletSrcSet} />
            <img alt={images[0].alt} src={images[0].imgSrc} />
          </picture>
        </div>
      )}
      {hasPagination && (
        <div className="c-sky-it-panel__pagination">
          <div className="c-pagination c-pagination--sky-it">
            <ul className="c-pagination__list">
              {paginationItems.map((item, index) => (
                <li key={item.linkLabel} className={`c-pagination__item${selectedPage === index ? ' is-active' : ''}`}>
                  <a className="c-pagination__link" onClick={() => sliderInstance.current.goTo(index * 1)}>
                    {item.linkLabel}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

SkyItPanel.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      mobileSrcSet: PropTypes.string.isRequired,
      tabletSrcSet: PropTypes.string.isRequired,
      imgSrc: PropTypes.string.isRequired,
      alt: PropTypes.string,
    })
  ),
  intcmpCta: PropTypes.string,
  ctaPrimaryLabel: PropTypes.string,
  ctaSecondaryLabel: PropTypes.string,
  ctaPrimaryHref: PropTypes.string,
  onCtaPrimaryClick: PropTypes.func,
  onCtaSecondaryClick: PropTypes.func,
  hasBorder: PropTypes.bool,
  hasImageCentered: PropTypes.bool,
  paginationItems: PropTypes.arrayOf(
    PropTypes.shape({
      linkLabel: PropTypes.string,
      onClick: PropTypes.func,
    })
  ),
  hasInfo: PropTypes.bool,
  isReversed: PropTypes.bool,
  selectedPage: PropTypes.number,
};

export default SkyItPanel;
